import { computed, reactive, ref, watch } from 'vue'
import { merge } from 'lodash'

import events$ from '@shared/services/Events'

import useI18n from '@shared/hooks/useI18n'

import { getLangTranslations, setLangTranslations } from '@shared/plugins/i18n'
import { getAllContractStartDates, isProductReloadNecessary } from '@shared/utils/BasketConfig'
import { mapIdToProductId } from '@shared/utils/Product'
import { ReactivityUtil } from '@shared/utils/Reactivity'
import { filterSoftCloseProductsHaX } from '@shared/utils/HaX'

import productAPI from '@shared/api/product'

import { PACKAGE } from '@shared/config/constants'
import { EVENT_PRODUCT } from '@shared/config/events'

// HOOKS
const { getContent } = useI18n()

// DATA
const initialized = ref(false)

const store = reactive({
  categories: {},
  groups: {},
  products: {},
})

function productStructureStore() {
  // COMPUTED
  const categories = computed(() => {
    return store.categories
  })

  const groups = computed(() => {
    return store.groups
  })

  const products = computed(() => {
    const data = ReactivityUtil.clone(store.products)

    Object.keys(store.products).forEach(__productId => {
      mapIdToProductId(data, __productId)
    })

    return data
  })

  // METHODS

  function ready() {
    return new Promise(function (resolve) {
      if (initialized.value) resolve()

      const unwatch = watch(
        () => initialized.value,
        () => {
          unwatch()
          resolve()
        }
      )
    })
  }

  /**
   * initProducts loads productStructure, if products need to be loaded
   * caveat: also get's called on initial local basket-initialization, but cancels, because masterTreaty is missing
   *
   * @param {Basket} __basket
   * @param {Basket} originalBasket
   */
  async function installProductStructureOnUpdate(basket, originalBasket) {
    if (basket?.persons?.length === 0) return

    const __reloadNecessary = !!isProductReloadNecessary(basket, originalBasket)
    if ((__reloadNecessary || initialized.value === false) && basket.masterTreaty) {

      const startDates = getAllContractStartDates(basket)

      await getAndSetProducts({
        collectiveChannel: basket.collective?.channel,
        collectiveContractNumber: basket.collective?.contractNumber,
        contractStartDates: Array.from(startDates),
        masterTreaty: basket.masterTreaty,
      }, basket.language)
    }
  }

  async function getAndSetProducts(payload, language) {
    const structure = await productAPI.getProducts(payload)

    if (structure) {
      setProducts(structure)

      const content = getLangTranslations(language) ?? {}

      // @note: content for products
      if (!content?.[PACKAGE.PRODUCTS]) {
        const _contentFile = await getContent({ app: PACKAGE.PRODUCTS, language })
        if (_contentFile.changed) {
          merge(content, _contentFile.content)
          setLangTranslations(language, content)
        }
      }

      initialized.value = true
    }
  }

  /**
   *  setProducts takes a productStructure and stores it in the local data
   *  @param {ProductStructure} payload
   */
  function setProducts(payload) {
    /*
     *  HaX for soft close:
     *  We filter out soft closed products (classic, family, jump)
     *  and store these filtered products in the original productData
     *  (as we no longer want to show these products to the users)
     *  But as we need the original (non-filtered) products for the
     *  existing customers to eventually replace the newly-selected vital
     *  product with the existing soft closed product, we store them
     *  in the haXProducts
     */
    const __payload = filterSoftCloseProductsHaX(ReactivityUtil.clone(payload))
    ReactivityUtil.reAssign(store, __payload)

    events$.emit(EVENT_PRODUCT.STRUCTURE_SET, __payload)
  }

  return {
    categories,
    ready,
    getAndSetProducts,
    initialized,
    installProductStructureOnUpdate,
    groups,
    products,
  }
}

export default productStructureStore()
